'use client';

import Link from 'next/link';
import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { InlineLanguageSwitcher } from '@/components/InlineLanguageSwitcher/InlineLanguageSwitcher';
import { ThemeSwitcher } from '@/components/ThemeSwitcher/ThemeSwitcher';
import { CrossIcon, MenuIcon } from '@/components/ui/Icons/Icons';
import { SocialLinks } from '@/components/ui/SocialLinks/SocialLinks';
import { CONFIG } from '@/config';
import { useStaticPages } from '@/hooks/useStaticPages';

import * as S from './BurgerMenu.styles';

interface BurgerMenuProps {
  isOpen: boolean;
  handleToggleBurger: () => void;
}

const {
  contacts: { tel, telUrl, email, emailUrl }
} = CONFIG;

export const BurgerMenu: FC<BurgerMenuProps> = ({
  isOpen,
  handleToggleBurger
}) => {
  const { t } = useTranslation();
  const { pages } = useStaticPages();

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  return (
    <>
      <S.BurgerButton data-cy="header-burger-menu" onClick={handleToggleBurger}>
        {isOpen ? <CrossIcon /> : <MenuIcon />}
      </S.BurgerButton>

      {isOpen && <S.BurgerOverlay onClick={handleToggleBurger} />}

      <S.Content $isOpen={isOpen}>
        <S.MenuLinksWrapper>
          {pages.map(page => (
            <S.MenuLink
              key={page.slug}
              as={Link}
              data-cy={`burger-menu-link-${page.slug}`}
              href={page.href}
              prefetch={false}
            >
              {page.title}
            </S.MenuLink>
          ))}
        </S.MenuLinksWrapper>

        <S.ContactsAndLanguageWrapper>
          <InlineLanguageSwitcher />

          <ThemeSwitcher />

          <S.ContactsWrapper>
            <S.ContactsTitle>{t('BurgerMenu.contacts_title')}</S.ContactsTitle>

            <S.Contacts>
              <S.MenuLink as={Link} data-cy="burger-menu-tel" href={telUrl}>
                {tel}
              </S.MenuLink>
              <S.MenuLink as={Link} data-cy="burger-menu-email" href={emailUrl}>
                {email}
              </S.MenuLink>
            </S.Contacts>

            <SocialLinks />
          </S.ContactsWrapper>
        </S.ContactsAndLanguageWrapper>
      </S.Content>
    </>
  );
};
